import { useState } from "react";

import { useFiles, useUploadFile } from "../api/files.api.ts";
import { useIngestJira, useIngestJunit } from "../api/ingest.api.ts";
import { Badge, Button, Card, Page } from "../components/Page.tsx";

export function Upload() {
  const upload = useUploadFile();
  const files = useFiles();
  const ingestJunit = useIngestJunit();
  const ingestJira = useIngestJira();

  const [junitText, setJunitText] = useState("");
  const [jiraText, setJiraText] = useState("");
  const [jiraError, setJiraError] = useState<string | null>(null);

  return (
    <Page
      title="Upload"
      description="Bring in tests, tickets, and documents. The samples in the repo work as a quick demo."
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="JUnit XML">
          <p className="text-sm text-zinc-600">
            Paste a JUnit XML report. Sample:{" "}
            <code className="text-xs">samples/junit/checkout-tests.xml</code>.
          </p>
          <textarea
            value={junitText}
            onChange={(e) => setJunitText(e.target.value)}
            placeholder="<?xml version=&quot;1.0&quot;?>&#10;<testsuites>&#10;  ..."
            className="mt-3 h-40 w-full rounded-md border border-zinc-300 bg-white p-2 font-mono text-xs"
          />
          <div className="mt-3 flex items-center gap-3">
            <Button
              onClick={() => ingestJunit.mutate({ xml: junitText })}
              disabled={!junitText.trim() || ingestJunit.isPending}
            >
              {ingestJunit.isPending ? "Ingesting…" : "Ingest JUnit"}
            </Button>
            {ingestJunit.data && (
              <span className="text-xs text-zinc-600">
                {ingestJunit.data.testCasesUpserted} cases ·{" "}
                <span className="text-emerald-700">
                  {ingestJunit.data.passed} passed
                </span>{" "}
                ·{" "}
                <span className="text-red-700">
                  {ingestJunit.data.failed} failed
                </span>{" "}
                ·{" "}
                <span className="text-amber-700">
                  {ingestJunit.data.skipped} skipped
                </span>
              </span>
            )}
            {ingestJunit.error && (
              <span className="text-xs text-red-700">
                {String((ingestJunit.error as Error).message)}
              </span>
            )}
          </div>
        </Card>

        <Card title="Jira JSON">
          <p className="text-sm text-zinc-600">
            Paste a Jira issues export. Sample:{" "}
            <code className="text-xs">samples/jira/tickets.json</code>.
          </p>
          <textarea
            value={jiraText}
            onChange={(e) => setJiraText(e.target.value)}
            placeholder={'{ "issues": [...] }'}
            className="mt-3 h-40 w-full rounded-md border border-zinc-300 bg-white p-2 font-mono text-xs"
          />
          <div className="mt-3 flex items-center gap-3">
            <Button
              onClick={() => {
                setJiraError(null);
                try {
                  const parsed = JSON.parse(jiraText) as {
                    issues: unknown[];
                  };
                  ingestJira.mutate({
                    source: "jira",
                    issues: parsed.issues as never,
                  });
                } catch (err) {
                  setJiraError(
                    `JSON parse error: ${err instanceof Error ? err.message : String(err)}`,
                  );
                }
              }}
              disabled={!jiraText.trim() || ingestJira.isPending}
            >
              {ingestJira.isPending ? "Ingesting…" : "Ingest Jira"}
            </Button>
            {ingestJira.data && (
              <span className="text-xs text-zinc-600">
                {ingestJira.data.ticketsUpserted} upserted (
                {Object.entries(ingestJira.data.byType)
                  .map(([k, v]) => `${v} ${k}`)
                  .join(", ")}
                )
              </span>
            )}
            {(ingestJira.error || jiraError) && (
              <span className="text-xs text-red-700">
                {jiraError ??
                  String((ingestJira.error as Error).message)}
              </span>
            )}
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Card title="Documents (PDF, Markdown, text)">
          <p className="text-sm text-zinc-600">
            Drop a file. Up to 50 MB. Text and Markdown are read directly; PDFs are extracted via{" "}
            <code className="text-xs">unpdf</code>. Each file is chunked at 500 tokens with 50-token overlap.
          </p>
          <input
            type="file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
            }}
            className="mt-3 block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-zinc-900 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-zinc-800"
          />
          {upload.data && (
            <div className="mt-3 text-sm">
              Stored{" "}
              <code className="text-xs">{upload.data.filename}</code> (
              {(upload.data.byteSize / 1024).toFixed(1)} KB){" "}
              {upload.data.deduplicated ? (
                <Badge tone="warn">deduplicated</Badge>
              ) : (
                <Badge tone="success">
                  {upload.data.chunksCreated} chunks
                </Badge>
              )}
            </div>
          )}
          {upload.error && (
            <div className="mt-3 text-sm text-red-700">
              {String((upload.error as Error).message)}
            </div>
          )}

          <div className="mt-5 border-t border-zinc-100 pt-4">
            <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">
              Already uploaded
            </div>
            <ul className="mt-2 divide-y divide-zinc-100">
              {(files.data?.files ?? []).map((f) => (
                <li
                  key={f.id}
                  className="flex items-center justify-between py-2 text-sm"
                >
                  <span>
                    <span className="font-mono text-xs text-zinc-500">
                      {f.id.slice(0, 8)}
                    </span>{" "}
                    {f.filename}
                  </span>
                  <span className="text-xs text-zinc-500">
                    {f.chunkCount} chunks · {(f.byteSize / 1024).toFixed(1)} KB
                  </span>
                </li>
              ))}
              {(files.data?.files ?? []).length === 0 && (
                <li className="py-2 text-sm text-zinc-500">No files yet.</li>
              )}
            </ul>
          </div>
        </Card>
      </div>
    </Page>
  );
}
